import React, { useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { ButtonLoading, FormInput } from "go-form";
import useFlash from "go-alert/AlertMessage";
import handleError from "go-app/services/errors";
import { FormSelectGroup } from "go-form/components/FormSelect";
import { selectOrganization } from "go-security/services/organizations/selectors";
import { CurrencyApi } from "../../../../../../../../../services/Api/Organization/types";
import { api } from "../../../../../../../../../services/Api/api";

interface Props {
	currency: CurrencyApi;
	onHide: () => void;
	handleSave: (currency: CurrencyApi) => void;
}

const CurrencyModalForm = (props: Props): JSX.Element => {
	const organization = useSelector(selectOrganization);
	const form = useForm<CurrencyApi>({
		criteriaMode: "all",
		defaultValues: { ...props.currency, currency_to: organization.currency },
	});
	const { addFlash, addSuccessFlash } = useFlash();
	const {
		register,
		handleSubmit,
		formState: { errors },
		setError,
		control,
	} = form;
	const [loading, setLoading] = useState(false);
	const { t } = useTranslation();

	const onSubmit = handleSubmit(async (data: CurrencyApi) => {
		setLoading(true);
		data.id = props.currency.id;
		data.currency_to = organization.currency || data.currency_to;
		try {
			const res = await api.organization().createOrUpdateCurrency(data);
			addSuccessFlash(t("common.flash.saved", { ns: "lib" }));
			props.handleSave(res);
		} catch (err) {
			handleError.form(err, setError, addFlash);
		}
		setLoading(false);
	});
	const options = [
		{ value: "PLN", label: "PLN" },
		{ value: "EUR", label: "EUR" },
		{ value: "USD", label: "USD" },
		{ value: "GBP", label: "GBP" },
		{ value: "CZK", label: "CZK" },
	];
	const currency = props.currency;

	return (
		<Modal show={!!currency} onHide={props.onHide}>
			<Form key="user-form" noValidate onSubmit={onSubmit}>
				<Modal.Header closeButton>
					<Modal.Title>
						{currency.id ? (
							<>
								{t("modules.currency.header.title")} {t("common.word.from", { ns: "lib" })}{" "}
								{currency.currency_from} {t("common.word.to", { ns: "lib" })} {currency.currency_to}
							</>
						) : (
							<>{t("modules.currency.header.new.title")}</>
						)}
					</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<FormSelectGroup
						label={t("modules.currency.field.currency_from.title")}
						name="currency_from"
						errors={errors}
						options={options}
						defaultValue={currency.currency_from}
						control={control}
						data-testid="currency_from"
					/>
					<FormInput
						disabled={true}
						label={t("modules.currency.field.currency_to.title")}
						register={register}
						name="currency_to"
						errors={errors}
					/>
					<FormInput
						label={t("modules.currency.field.rate.title")}
						register={register}
						name="rate"
						errors={errors}
					/>
				</Modal.Body>
				<Modal.Footer>
					<ButtonLoading loading={loading} variant="primary" type="submit" onClick={onSubmit}>
						{t("common.action.save", { ns: "lib" })}
					</ButtonLoading>
					<Button variant="light" onClick={props.onHide}>
						{t("common.action.cancel", { ns: "lib" })}
					</Button>
				</Modal.Footer>
			</Form>
		</Modal>
	);
};
export default CurrencyModalForm;
